// Service Google Drive pour la synchronisation des tâches et preuves
import googleAuthService from './googleAuth';

const APP_FOLDER_NAME = 'ADHD Tasks Manager';
const TASKS_FILE_NAME = 'tasks.json';
const PROOFS_FOLDER_NAME = 'Preuves';
const EXPORTS_FOLDER_NAME = 'Exports';

// Catégories pour les sous-dossiers
const CATEGORY_FOLDERS = {
  vital: 'Vital (Médicaments & Santé)',
  chore: 'Corvées (Travail & Ménage)',
  pleasure: 'Plaisir (Détente & Loisirs)',
  reward: 'Récompenses'
};

class GoogleDriveService {
  constructor() {
    this.appFolderId = null;
    this.proofsFolderId = null;
    this.exportsFolderId = null;
    this.tasksFileId = null;
    this.lastSync = null;
    // Cache des dossiers créés
    this.folderCache = new Map();
  }

  // Vérifier si le service est prêt
  isReady() {
    return googleAuthService.checkSignedIn() &&
           googleAuthService.isApiAvailable('drive') &&
           window.gapi?.client?.drive?.files;
  }

  // Initialiser le dossier de l'application
  async initAppFolder() {
    if (!googleAuthService.checkSignedIn()) {
      throw new Error('Non connecté à Google');
    }

    // Vérifier que l'API Drive est disponible
    if (!googleAuthService.isApiAvailable('drive')) {
      throw new Error('API Google Drive non activée');
    }

    // Vérifier que GAPI Drive client est chargé
    if (!window.gapi?.client?.drive?.files) {
      throw new Error('GAPI Drive client non chargé');
    }

    try {
      // Chercher ou créer le dossier principal
      this.appFolderId = await this.findOrCreateFolder(APP_FOLDER_NAME, 'root');

      // Chercher ou créer le dossier des preuves
      this.proofsFolderId = await this.findOrCreateFolder(PROOFS_FOLDER_NAME, this.appFolderId);

      // Chercher ou créer le dossier des exports
      this.exportsFolderId = await this.findOrCreateFolder(EXPORTS_FOLDER_NAME, this.appFolderId);

      // Chercher le fichier des tâches
      this.tasksFileId = await this.findFile(TASKS_FILE_NAME, this.appFolderId);

      // Créer les sous-dossiers par catégorie dans Preuves
      for (const [categoryId, categoryName] of Object.entries(CATEGORY_FOLDERS)) {
        const categoryFolderId = await this.findOrCreateFolder(categoryName, this.proofsFolderId);
        this.folderCache.set(`category:${categoryId}`, categoryFolderId);
      }

      console.log('Google Drive initialisé:', {
        appFolder: this.appFolderId,
        proofsFolder: this.proofsFolderId,
        exportsFolder: this.exportsFolderId,
        tasksFile: this.tasksFileId
      });

      return true;
    } catch (error) {
      console.error('Erreur initialisation Google Drive:', error);
      throw error;
    }
  }

  // Trouver ou créer un dossier
  async findOrCreateFolder(name, parentId) {
    // Vérifier le cache
    const cacheKey = `${parentId}:${name}`;
    if (this.folderCache.has(cacheKey)) {
      return this.folderCache.get(cacheKey);
    }

    // Chercher le dossier existant
    const query = parentId === 'root'
      ? `name='${name}' and mimeType='application/vnd.google-apps.folder' and 'root' in parents and trashed=false`
      : `name='${name}' and mimeType='application/vnd.google-apps.folder' and '${parentId}' in parents and trashed=false`;

    const response = await window.gapi.client.drive.files.list({
      q: query,
      fields: 'files(id, name)',
      spaces: 'drive',
    });

    if (response.result.files && response.result.files.length > 0) {
      const folderId = response.result.files[0].id;
      this.folderCache.set(cacheKey, folderId);
      return folderId;
    }

    // Créer le dossier
    const fileMetadata = {
      name: name,
      mimeType: 'application/vnd.google-apps.folder',
      parents: parentId === 'root' ? undefined : [parentId],
    };

    const createResponse = await window.gapi.client.drive.files.create({
      resource: fileMetadata,
      fields: 'id',
    });

    const newFolderId = createResponse.result.id;
    this.folderCache.set(cacheKey, newFolderId);
    return newFolderId;
  }

  // Obtenir ou créer le dossier de date (YYYY-MM)
  async getDateFolder(parentId, date) {
    const dateObj = new Date(date);
    const yearMonth = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
    const folderName = yearMonth;

    const cacheKey = `date:${parentId}:${yearMonth}`;
    if (this.folderCache.has(cacheKey)) {
      return this.folderCache.get(cacheKey);
    }

    const folderId = await this.findOrCreateFolder(folderName, parentId);
    this.folderCache.set(cacheKey, folderId);
    return folderId;
  }

  // Obtenir ou créer le dossier de jour (DD - Jour)
  async getDayFolder(parentId, date) {
    const dateObj = new Date(date);
    const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const day = String(dateObj.getDate()).padStart(2, '0');
    const dayName = dayNames[dateObj.getDay()];
    const folderName = `${day} - ${dayName}`;

    const cacheKey = `day:${parentId}:${dateObj.toISOString().split('T')[0]}`;
    if (this.folderCache.has(cacheKey)) {
      return this.folderCache.get(cacheKey);
    }

    const folderId = await this.findOrCreateFolder(folderName, parentId);
    this.folderCache.set(cacheKey, folderId);
    return folderId;
  }

  // Trouver un fichier dans un dossier
  async findFile(name, parentId) {
    const query = `name='${name}' and '${parentId}' in parents and trashed=false`;

    const response = await window.gapi.client.drive.files.list({
      q: query,
      fields: 'files(id, name, modifiedTime)',
      spaces: 'drive',
    });

    if (response.result.files && response.result.files.length > 0) {
      return response.result.files[0].id;
    }

    return null;
  }

  // Sauvegarder les tâches dans Google Drive
  async saveTasks(tasks) {
    if (!this.isReady()) {
      throw new Error('Non connecté à Google');
    }

    if (!this.appFolderId) {
      await this.initAppFolder();
    }

    const content = JSON.stringify({
      version: 1,
      exportedAt: new Date().toISOString(),
      tasks: tasks,
    }, null, 2);

    const blob = new Blob([content], { type: 'application/json' });

    try {
      if (this.tasksFileId) {
        // Mettre à jour le fichier existant
        await this.updateFile(this.tasksFileId, blob);
      } else {
        // Créer un nouveau fichier
        this.tasksFileId = await this.createFile(TASKS_FILE_NAME, blob, this.appFolderId, 'application/json');
      }

      this.lastSync = new Date();
      console.log('Tâches synchronisées avec Google Drive');
      return true;
    } catch (error) {
      console.error('Erreur sauvegarde tâches:', error);
      throw error;
    }
  }

  // Charger les tâches depuis Google Drive
  async loadTasks() {
    if (!this.isReady()) {
      throw new Error('Non connecté à Google');
    }

    if (!this.appFolderId) {
      await this.initAppFolder();
    }

    if (!this.tasksFileId) {
      console.log('Aucun fichier de tâches trouvé sur Drive');
      return null;
    }

    try {
      const response = await window.gapi.client.drive.files.get({
        fileId: this.tasksFileId,
        alt: 'media',
      });

      const data = typeof response.body === 'string'
        ? JSON.parse(response.body)
        : response.result;

      console.log('Tâches chargées depuis Google Drive:', data.tasks?.length || 0);
      return data.tasks || [];
    } catch (error) {
      console.error('Erreur chargement tâches:', error);
      throw error;
    }
  }

  // Créer un fichier
  async createFile(name, blob, parentId, mimeType) {
    const metadata = {
      name: name,
      mimeType: mimeType,
      parents: [parentId],
    };

    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', blob);

    const accessToken = googleAuthService.getAccessToken();

    const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: form,
    });

    const result = await response.json();
    return result.id;
  }

  // Mettre à jour un fichier
  async updateFile(fileId, blob) {
    const accessToken = googleAuthService.getAccessToken();

    await fetch(`https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': blob.type,
      },
      body: blob,
    });
  }

  /**
   * Uploader une image de preuve avec organisation par dossiers
   * Structure: ADHD Tasks Manager / Preuves / [Catégorie] / [YYYY-MM] / [DD - Jour] / preuve.jpg
   *
   * @param {string} imageData - Base64 de l'image
   * @param {string} taskId - ID de la tâche
   * @param {string} proofId - ID de la preuve
   * @param {Object} taskInfo - Infos de la tâche (category, task, date)
   */
  async uploadProofImage(imageData, taskId, proofId, taskInfo = {}) {
    console.log('📤 uploadProofImage démarré:', { taskId, proofId, taskInfo });

    if (!this.isReady()) {
      console.error('❌ Service Drive non prêt:', {
        signedIn: window.gapi?.client?.getToken?.(),
        driveClient: !!window.gapi?.client?.drive?.files
      });
      throw new Error('Non connecté à Google Drive');
    }

    if (!this.proofsFolderId) {
      console.log('📁 Init app folder...');
      await this.initAppFolder();
    }

    try {
      const category = taskInfo.category || 'chore';
      const taskName = taskInfo.task || 'Tâche';
      const taskDate = taskInfo.date || new Date().toISOString().split('T')[0];
      console.log('📁 Création structure dossiers:', { category, taskName, taskDate });

      // Obtenir le dossier de catégorie
      const categoryFolderName = CATEGORY_FOLDERS[category] || CATEGORY_FOLDERS.chore;
      console.log('📁 Recherche dossier catégorie:', categoryFolderName);
      const categoryFolderId = await this.findOrCreateFolder(categoryFolderName, this.proofsFolderId);
      console.log('✅ Dossier catégorie:', categoryFolderId);

      // Obtenir le dossier du mois (YYYY-MM)
      const monthFolderId = await this.getDateFolder(categoryFolderId, taskDate);
      console.log('✅ Dossier mois:', monthFolderId);

      // Obtenir le dossier du jour (DD - Jour)
      const dayFolderId = await this.getDayFolder(monthFolderId, taskDate);
      console.log('✅ Dossier jour:', dayFolderId);

      // Convertir base64 en blob
      console.log('📷 Conversion image en blob...');
      const response = await fetch(imageData);
      const blob = await response.blob();
      console.log('✅ Blob créé:', blob.size, 'bytes');

      // Générer le nom du fichier avec l'heure
      const now = new Date();
      const timeStr = now.toTimeString().slice(0, 5).replace(':', 'h');
      const cleanTaskName = taskName
        .replace(/[<>:"/\\|?*]/g, '') // Supprimer caractères interdits
        .substring(0, 30); // Limiter la longueur
      const fileName = `${timeStr} - ${cleanTaskName}.jpg`;
      console.log('📄 Nom fichier:', fileName);

      // Uploader le fichier
      console.log('📤 Upload fichier vers Drive...');
      const fileId = await this.createFile(fileName, blob, dayFolderId, 'image/jpeg');
      console.log('✅ Fichier uploadé, ID:', fileId);

      // Obtenir le lien de partage
      await window.gapi.client.drive.permissions.create({
        fileId: fileId,
        resource: {
          role: 'reader',
          type: 'anyone',
        },
      });

      const fileInfo = await window.gapi.client.drive.files.get({
        fileId: fileId,
        fields: 'webViewLink, webContentLink, name',
      });

      console.log('📸 Preuve uploadée:', {
        fileName: fileInfo.result.name,
        category: categoryFolderName,
        date: taskDate,
        fileId: fileId
      });

      return {
        fileId: fileId,
        fileName: fileInfo.result.name,
        webViewLink: fileInfo.result.webViewLink,
        webContentLink: fileInfo.result.webContentLink,
        category: category,
        date: taskDate,
        taskName: taskName
      };
    } catch (error) {
      console.error('Erreur upload image:', error);
      throw error;
    }
  }

  // Supprimer un fichier
  async deleteFile(fileId) {
    if (!this.isReady()) return;

    try {
      await window.gapi.client.drive.files.delete({
        fileId: fileId,
      });
    } catch (error) {
      console.error('Erreur suppression fichier:', error);
    }
  }

  // Exporter toutes les données en CSV
  async exportToCSV(tasks, completedTasks, proofs) {
    if (!this.isReady()) {
      throw new Error('Non connecté à Google');
    }

    if (!this.appFolderId) {
      await this.initAppFolder();
    }

    // Générer le contenu CSV
    const headers = [
      'ID', 'Date', 'Heure', 'Tâche', 'Description', 'Catégorie',
      'Durée (min)', 'Points', 'Sous-tâches', 'Complétée', 'Preuves', 'Créée le'
    ];

    const rows = tasks.map(task => [
      task.id,
      task.date || '',
      task.time || '',
      task.task || '',
      (task.desc || '').replace(/[\n\r\t]/g, ' '),
      task.category || '',
      task.duration || 30,
      task.points || 0,
      (task.instructions || []).join(' | '),
      completedTasks[task.id] ? 'Oui' : 'Non',
      (proofs.filter(p => p.taskId === task.id).length) || 0,
      task.createdAt || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' });

    // Organiser les exports par mois
    const now = new Date();
    const monthFolder = await this.getDateFolder(this.exportsFolderId, now.toISOString());
    const fileName = `export_${now.toISOString().split('T')[0]}_${now.toTimeString().slice(0,5).replace(':','h')}.csv`;

    const fileId = await this.createFile(fileName, blob, monthFolder, 'text/csv');

    console.log('📊 Export CSV créé:', fileName);
    return fileId;
  }

  // Obtenir la structure des dossiers (pour debug/affichage)
  async getFolderStructure() {
    if (!this.appFolderId) {
      await this.initAppFolder();
    }

    return {
      appFolder: {
        id: this.appFolderId,
        name: APP_FOLDER_NAME
      },
      proofsFolder: {
        id: this.proofsFolderId,
        name: PROOFS_FOLDER_NAME,
        categories: Object.entries(CATEGORY_FOLDERS).map(([id, name]) => ({
          id,
          name,
          folderId: this.folderCache.get(`category:${id}`)
        }))
      },
      exportsFolder: {
        id: this.exportsFolderId,
        name: EXPORTS_FOLDER_NAME
      },
      tasksFile: {
        id: this.tasksFileId,
        name: TASKS_FILE_NAME
      }
    };
  }

  // Obtenir l'état de synchronisation
  getSyncStatus() {
    return {
      isConnected: this.isReady(),
      lastSync: this.lastSync,
      appFolderId: this.appFolderId,
      tasksFileId: this.tasksFileId,
    };
  }
}

// Singleton
const googleDriveService = new GoogleDriveService();
export default googleDriveService;
